/**
 * Organization Types for SSP Enterprise App
 */

export type OrgRole = 'owner' | 'admin' | 'member' | 'viewer';
export type InviteStatus = 'pending' | 'accepted' | 'rejected' | 'revoked' | 'expired';

export interface OrganizationSettings {
  allowMemberInvites: boolean;
  spamFilterEnabled: boolean;
  /** Members may create read-only share links for payments (absent = on). */
  allowProposalSharing?: boolean;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  orgIndex: number;
  description?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  settings: OrganizationSettings;
}

export type MemberAccountStatus = 'pending_wk' | 'pending_email' | 'complete';

export interface Membership {
  id: string;
  organizationId: string;
  wkIdentity?: string;
  email?: string;
  displayName?: string;
  role: OrgRole;
  joinedAt: string;
  invitedBy?: string;
  accountStatus?: MemberAccountStatus;
}

export interface Invitation {
  id: string;
  organizationId: string;
  organizationName?: string;
  inviteeWkIdentity?: string;
  inviteeEmail?: string;
  invitedBy: string;
  role: Exclude<OrgRole, 'owner'>;
  status: InviteStatus;
  createdAt: string;
  expiresAt: string;
  respondedAt?: string;
  message?: string;
}

export interface OrganizationWithMembership extends Organization {
  membership: Membership;
}

export interface OrganizationDetail extends Organization {
  membership: Membership;
  memberCount: number;
}

export type Member = Membership;

// API Request types
export interface CreateOrganizationRequest {
  name: string;
  description?: string;
}

export interface UpdateOrganizationRequest {
  name?: string;
  description?: string;
  settings?: Partial<OrganizationSettings>;
}

export interface CreateInvitationRequest {
  inviteeWkIdentity?: string;
  inviteeEmail?: string;
  role: 'member' | 'viewer';
  message?: string;
}

export interface UpdateMemberRoleRequest {
  role: OrgRole;
}

// API Response types
export interface OrganizationResponse {
  success: boolean;
  organization?: Organization;
  error?: string;
  errorCode?: string;
}

export interface OrganizationsListResponse {
  success: boolean;
  organizations?: OrganizationWithMembership[];
  error?: string;
}

export interface OrganizationDetailResponse {
  success: boolean;
  organization?: OrganizationDetail;
  error?: string;
  errorCode?: string;
}

export interface MembersListResponse {
  success: boolean;
  members?: Member[];
  error?: string;
}

export interface InvitationsListResponse {
  success: boolean;
  invitations?: Invitation[];
  error?: string;
}

export interface InvitationResponse {
  success: boolean;
  invitation?: Invitation;
  error?: string;
  errorCode?: string;
}

export interface MembershipResponse {
  success: boolean;
  membership?: Membership;
  error?: string;
  errorCode?: string;
}

export interface SimpleResponse {
  success: boolean;
  error?: string;
  errorCode?: string;
}

// ============================================================
// Activity & Analytics Types
// ============================================================

export type OrgAuditEventType =
  | 'org_created'
  | 'org_updated'
  | 'org_deleted'
  | 'member_joined'
  | 'member_left'
  | 'member_removed'
  | 'member_role_changed'
  | 'invitation_created'
  | 'invitation_accepted'
  | 'invitation_rejected'
  | 'invitation_revoked'
  | 'invitation_expired'
  | 'ownership_transferred'
  | 'member_upgraded';

export interface OrgAuditLog {
  id: string;
  organizationId: string;
  eventType: OrgAuditEventType;
  actorWkIdentity: string;
  targetWkIdentity?: string;
  details: Record<string, unknown>;
  createdAt: string;
  ip?: string;
  userAgent?: string;
}

// Re-export from canonical source to avoid duplication
import type { CriticalActionType } from './criticalAction';
export type { CriticalActionType } from './criticalAction';

export type CriticalActionPhase =
  | 'challenge_requested'
  | 'challenge_used'
  | 'verification_failed'
  | 'action_completed';

export interface CriticalActionLogEntry {
  id: string;
  timestamp: string;
  action: CriticalActionType;
  wkIdentity: string;
  targetOrgId?: string;
  targetWkIdentity?: string;
  phase: CriticalActionPhase;
  success: boolean;
  errorCode?: string;
  errorMessage?: string;
  ip?: string;
  userAgent?: string;
}

export type LoginMethod = 'wk' | 'email' | 'google';

export interface LoginAttemptEntry {
  id: string;
  timestamp: string;
  wkIdentity?: string;
  email?: string;
  loginMethod: LoginMethod;
  success: boolean;
  ip?: string;
  userAgent?: string;
  errorCode?: string;
  durationMs?: number;
}

// Query param interfaces
export interface AuditLogQueryParams {
  limit?: number;
  skip?: number;
  eventType?: OrgAuditEventType;
  actorWkIdentity?: string;
  startDate?: string;
  endDate?: string;
}

export interface CriticalActionsQueryParams {
  action?: CriticalActionType;
  success?: boolean;
  startDate?: string;
  endDate?: string;
  limit?: number;
  skip?: number;
}

export interface LoginActivityQueryParams {
  loginMethod?: LoginMethod;
  success?: boolean;
  startDate?: string;
  endDate?: string;
  limit?: number;
  skip?: number;
}

// Response interfaces
export interface AuditLogsResponse {
  success: boolean;
  logs?: OrgAuditLog[];
  total?: number;
  error?: string;
}

export interface AuditStatsResponse {
  success: boolean;
  stats?: {
    totalEvents: number;
    byEventType: Record<string, number>;
    dailyCounts: { date: string; count: number }[];
    recentEvents: OrgAuditLog[];
  };
  error?: string;
}

export interface CriticalActionsResponse {
  success: boolean;
  logs?: CriticalActionLogEntry[];
  total?: number;
  stats?: {
    total: number;
    successful: number;
    failed: number;
    byAction: Record<string, number>;
  };
  error?: string;
}

export interface LoginActivityResponse {
  success: boolean;
  attempts?: LoginAttemptEntry[];
  total?: number;
  stats?: {
    total: number;
    successful: number;
    failed: number;
    byMethod: Record<string, number>;
  };
  error?: string;
}

// Display helper maps
export const auditEventLabels: Record<OrgAuditEventType, string> = {
  org_created: 'Organization Created',
  org_updated: 'Organization Updated',
  org_deleted: 'Organization Archived',
  member_joined: 'Member Joined',
  member_left: 'Member Left',
  member_removed: 'Member Removed',
  member_role_changed: 'Role Changed',
  invitation_created: 'Invitation Sent',
  invitation_accepted: 'Invitation Accepted',
  invitation_rejected: 'Invitation Rejected',
  invitation_revoked: 'Invitation Revoked',
  invitation_expired: 'Invitation Expired',
  ownership_transferred: 'Ownership Transferred',
  member_upgraded: 'Member Upgraded',
};

export const auditEventColors: Record<OrgAuditEventType, string> = {
  org_created: 'green',
  org_updated: 'blue',
  org_deleted: 'red',
  member_joined: 'green',
  member_left: 'orange',
  member_removed: 'red',
  member_role_changed: 'purple',
  invitation_created: 'blue',
  invitation_accepted: 'green',
  invitation_rejected: 'orange',
  invitation_revoked: 'red',
  invitation_expired: 'default',
  ownership_transferred: 'gold',
  member_upgraded: 'cyan',
};

export const criticalActionLabels: Record<CriticalActionType, string> = {
  transfer_ownership: 'Transfer Ownership',
  delete_organization: 'Archive Organization',
  remove_member: 'Remove Member',
  promote_to_admin: 'Promote to Admin',
  demote_from_admin: 'Demote from Admin',
  change_enterprise_email: 'Change Email',
  remove_enterprise_email: 'Remove Email',
  archive_vault: 'Archive Vault',
  unarchive_vault: 'Unarchive Vault',
  freeze_vault: 'Freeze Vault',
  unfreeze_vault: 'Unfreeze Vault',
  promote_vault_admin: 'Promote to Vault Admin',
  demote_vault_admin: 'Demote from Vault Admin',
};

export const criticalActionColors: Record<CriticalActionType, string> = {
  transfer_ownership: 'gold',
  delete_organization: 'red',
  remove_member: 'orange',
  promote_to_admin: 'cyan',
  demote_from_admin: 'orange',
  change_enterprise_email: 'blue',
  remove_enterprise_email: 'purple',
  archive_vault: 'orange',
  unarchive_vault: 'green',
  freeze_vault: 'red',
  unfreeze_vault: 'green',
  promote_vault_admin: 'purple',
  demote_vault_admin: 'orange',
};

export const loginMethodLabels: Record<LoginMethod, string> = {
  wk: 'WK Identity',
  email: 'Email Code',
  google: 'Google SSO',
};

// Role display helpers
export const roleLabels: Record<OrgRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
};

export const roleColors: Record<OrgRole, string> = {
  owner: 'gold',
  admin: 'blue',
  member: 'cyan',
  viewer: 'green',
};

// Permission helpers
export function hasRole(userRole: OrgRole, requiredRole: OrgRole): boolean {
  const roleHierarchy: Record<OrgRole, number> = {
    owner: 3,
    admin: 2,
    member: 1,
    viewer: 0,
  };
  return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
}

export function canInvite(role: OrgRole, allowMemberInvites: boolean): boolean {
  if (role === 'viewer') return false;
  return hasRole(role, 'admin') || (role === 'member' && allowMemberInvites);
}

export function canModifyMember(actorRole: OrgRole, targetRole: OrgRole): boolean {
  if (actorRole === 'owner') return true;
  if (actorRole === 'admin') return targetRole === 'member' || targetRole === 'viewer';
  return false;
}
